import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import AppShell from "../components/AppShell";
import TicketModal from "../components/TicketModal";
import { toast } from "sonner";
import {
  ArrowLeft, Ticket, Plus, CheckCircle, Fingerprint, Bell,
  Receipt, X, PaperPlaneTilt,
} from "@phosphor-icons/react";

const STATUS_STYLE = {
  RED: { text: "text-[#FF0033]", border: "border-[#FF0033]/60", glow: "glow-red", label: "UNPAID" },
  GREEN: { text: "text-[#00FF66]", border: "border-[#00FF66]/60", glow: "glow-green", label: "PAID" },
  PURPLE: { text: "text-[#9D00FF]", border: "border-[#9D00FF]/60", glow: "glow-purple", label: "REDEEMED" },
};

export default function MemberEventView() {
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIssue, setShowIssue] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(`/my/events/${eventId}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [eventId]);

  const markPaid = async (ticketId) => {
    try {
      await api.post(`/tickets/${ticketId}/pay`);
      toast.success("Payment confirmed → GREEN");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteTicket = async (ticketId) => {
    if (!window.confirm("Delete this ticket? Your allocation will be returned.")) return;
    try {
      await api.delete(`/tickets/${ticketId}`);
      toast.success("Ticket deleted; allocation returned");
      setSelectedTicket(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  if (loading) {
    return <AppShell><div className="mono-font text-sm text-white/40 py-16 text-center">Loading…</div></AppShell>;
  }
  if (!data) {
    return <AppShell><div className="mono-font text-sm text-white/60 py-16 text-center">Event not found or you are not a member.</div></AppShell>;
  }

  const { event, membership, tickets, requests } = data;
  const avail = membership.total_allocated - membership.issued_count;
  const canRequestMore = membership.allocation_cap > membership.total_allocated;

  return (
    <AppShell>
      <Link to="/member" data-testid="back-to-my-events" className="inline-flex items-center gap-2 mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 hover:text-white transition-colors duration-200 mb-6">
        <ArrowLeft size={12} /> Back to Portal
      </Link>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-white/10 bg-[#0A0A0C] overflow-hidden">
          <div className="aspect-[21/9] bg-black relative">
            {event.design_url ? (
              <img src={event.design_url} alt={event.title} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full aura-grid flex items-center justify-center">
                <Ticket size={64} weight="duotone" className="text-white/20" />
              </div>
            )}
          </div>
          <div className="p-6">
            <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Member Portal · {event.event_id}</div>
            <h1 className="heading-font text-3xl sm:text-4xl font-black tracking-tighter mt-2" data-testid="member-event-title">
              {event.title}
            </h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 mono-font text-xs text-white/60">
              {event.venue && <div>📍 {event.venue}</div>}
              {event.date && <div>🗓 {event.date}</div>}
              <div>Creator: <span className="text-white">{event.creator_name}</span></div>
            </div>
          </div>
        </div>

        <div className="border border-white/10 bg-[#0A0A0C] p-6 flex flex-col">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Your Allocation</div>
          <div className="mt-3 heading-font text-4xl font-black tracking-tighter">
            {avail} <span className="text-white/30 text-2xl">/ {membership.total_allocated}</span>
          </div>
          <div className="mono-font text-[10px] tracking-widest uppercase text-white/40 mt-1">Available · of your granted</div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            <div>
              <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Cap</div>
              <div className="mono-font text-lg text-white">{membership.allocation_cap}</div>
            </div>
            <div>
              <div className="mono-font text-[9px] tracking-widest uppercase text-white/40">Issued</div>
              <div className="mono-font text-lg text-white">{membership.issued_count}</div>
            </div>
          </div>
          <div className="mt-auto pt-6 grid grid-cols-1 gap-2">
            <button onClick={() => setShowIssue(true)} disabled={avail === 0}
              data-testid="member-issue-ticket-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
              <Plus size={14} weight="bold" /> Issue Ticket
            </button>
            <button onClick={() => setShowRequest(true)} disabled={!canRequestMore}
              data-testid="member-request-more-btn"
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-[#9D00FF]/60 text-[#9D00FF] mono-font text-xs uppercase tracking-[0.25em] hover:bg-[#9D00FF] hover:text-white transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
              <PaperPlaneTilt size={14} weight="bold" /> Request More Tickets
            </button>
          </div>
        </div>
      </div>

      {/* Requests history */}
      {requests && requests.length > 0 && (
        <div className="mb-8">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-2">
            <Bell size={12} /> Your Requests
          </div>
          <div className="border border-white/10 overflow-x-auto">
            <table className="terminal-table">
              <thead>
                <tr>
                  <th>Quantity</th>
                  <th>Note</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Resolved</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => {
                  const color = r.status === "PENDING" ? "text-[#FF9900] border-[#FF9900]/40"
                              : r.status === "APPROVED" ? "text-[#00FF66] border-[#00FF66]/40"
                              : "text-[#FF0033] border-[#FF0033]/40";
                  return (
                    <tr key={r.request_id} data-testid={`my-request-${r.request_id}`}>
                      <td className="text-white">+{r.quantity}</td>
                      <td className="text-white/70 max-w-xs truncate">{r.note || "—"}</td>
                      <td>
                        <span className={`inline-flex px-2 py-0.5 text-[10px] tracking-widest border ${color}`}>{r.status}</span>
                      </td>
                      <td className="text-white/60">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="text-white/40">{r.resolved_at ? new Date(r.resolved_at).toLocaleString() : "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 flex items-center gap-2">
        <Receipt size={12} /> Tickets You Distributed
      </div>
      {tickets.length === 0 ? (
        <div className="border border-dashed border-white/15 p-12 text-center">
          <Receipt size={40} weight="duotone" className="mx-auto text-white/40" />
          <div className="heading-font text-xl font-bold mt-4">No tickets issued yet</div>
          <div className="mono-font text-xs text-white/50 mt-2">Click "Issue Ticket" to distribute your first ticket.</div>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="terminal-table">
            <thead>
              <tr>
                <th>Ticket</th>
                <th>Buyer</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Checksum</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const s = STATUS_STYLE[t.color_code];
                return (
                  <tr key={t.ticket_id} data-testid={`member-ticket-row-${t.ticket_id}`}>
                    <td className="text-white">{t.ticket_id}</td>
                    <td>{t.buyer_name}</td>
                    <td className="text-white/60">{t.buyer_phone}</td>
                    <td>
                      <span className={`inline-flex items-center gap-2 px-2 py-1 border ${s.border} ${s.text} ${s.glow} text-[10px] tracking-widest`}>
                        <Fingerprint size={12} weight="fill" /> {t.color_code} · {s.label}
                      </span>
                    </td>
                    <td className="text-white/60">{t.token_hash}</td>
                    <td className="flex items-center gap-2">
                      <button onClick={() => setSelectedTicket(t)} data-testid={`member-view-ticket-${t.ticket_id}`}
                        className="px-3 py-1.5 border border-white/20 mono-font text-[10px] uppercase tracking-widest hover:border-white/60 transition-colors duration-200">
                        View
                      </button>
                      {t.color_code === "RED" && (
                        <button onClick={() => markPaid(t.ticket_id)} data-testid={`member-pay-ticket-${t.ticket_id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 border border-[#00FF66]/60 text-[#00FF66] mono-font text-[10px] uppercase tracking-widest hover:bg-[#00FF66] hover:text-black transition-colors duration-200">
                          <CheckCircle size={12} /> Mark Paid
                        </button>
                      )}
                      {t.color_code !== "PURPLE" && (
                        <button onClick={() => deleteTicket(t.ticket_id)} data-testid={`member-delete-ticket-${t.ticket_id}`}
                          title="Delete ticket"
                          className="p-1.5 border border-white/20 mono-font text-white/60 hover:border-[#FF0033]/60 hover:text-[#FF0033] transition-colors duration-200">
                          <X size={12} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <IssueTicketDialog open={showIssue} onClose={() => setShowIssue(false)} eventId={eventId}
        avail={avail} onDone={() => { setShowIssue(false); load(); }} />
      <RequestMoreDialog open={showRequest} onClose={() => setShowRequest(false)} eventId={eventId}
        remainingCap={membership.allocation_cap - membership.total_allocated}
        onDone={() => { setShowRequest(false); load(); }} />
      <TicketModal ticket={selectedTicket} event={event} open={!!selectedTicket}
        onClose={() => setSelectedTicket(null)}
        onPay={() => { markPaid(selectedTicket.ticket_id); setSelectedTicket(null); }}
        onDelete={() => deleteTicket(selectedTicket.ticket_id)} />
    </AppShell>
  );
}

function IssueTicketDialog({ open, onClose, eventId, avail, onDone }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/my/events/${eventId}/tickets`, { buyer_name: name, buyer_phone: phone });
      toast.success("Ticket issued (RED)");
      setName(""); setPhone("");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0A0A0C] border border-white/15 p-6">
        <div className="flex items-center justify-between">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Ticket Distribution</div>
          <button onClick={onClose} data-testid="member-issue-close" className="p-1 hover:bg-white/5">
            <X size={16} />
          </button>
        </div>
        <div className="heading-font text-2xl font-black tracking-tighter mt-1">Issue Ticket</div>
        <div className="mono-font text-xs text-white/50 mt-2">Available in your allocation: <span className="text-[#00FF66]">{avail}</span></div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Buyer Name</label>
            <input required value={name} onChange={(e) => setName(e.target.value)} data-testid="member-issue-name"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Buyer Phone</label>
            <input required value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="member-issue-phone"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#00FF66] transition-colors duration-200" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="member-issue-submit"
              className="px-5 py-2 bg-[#00FF66] text-black mono-font text-xs uppercase tracking-[0.25em] hover:bg-white transition-colors duration-200 disabled:opacity-60">
              {submitting ? "Issuing…" : "Issue Ticket"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function RequestMoreDialog({ open, onClose, eventId, remainingCap, onDone }) {
  const [quantity, setQuantity] = useState(10);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/my/events/${eventId}/requests`, { quantity: Number(quantity), note });
      toast.success("Request sent to event creator");
      setQuantity(10); setNote("");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-[#0A0A0C] border border-white/15 p-6">
        <div className="flex items-center justify-between">
          <div className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/40">Ticket Request</div>
          <button onClick={onClose} data-testid="member-request-close" className="p-1 hover:bg-white/5">
            <X size={16} />
          </button>
        </div>
        <div className="heading-font text-2xl font-black tracking-tighter mt-1 flex items-center gap-2">
          <PaperPlaneTilt size={22} weight="duotone" className="text-[#9D00FF]" /> Request More
        </div>
        <div className="mono-font text-xs text-white/50 mt-2">
          You can request up to <span className="text-[#00FF66]">{remainingCap}</span> more tickets within your cap.
        </div>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Quantity</label>
            <input required type="number" min={1} max={remainingCap} value={quantity} onChange={(e) => setQuantity(e.target.value)}
              data-testid="member-request-quantity"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#9D00FF] transition-colors duration-200" />
          </div>
          <div>
            <label className="mono-font text-[10px] tracking-[0.3em] uppercase text-white/50">Note (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="member-request-note"
              placeholder="Why do you need these extra tickets?"
              className="mt-2 w-full bg-transparent border border-white/15 px-4 py-3 mono-font text-sm focus:outline-none focus:border-[#9D00FF] transition-colors duration-200" />
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t border-white/10">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-white/20 mono-font text-xs uppercase tracking-[0.25em] hover:border-white/60 transition-colors duration-200">Cancel</button>
            <button type="submit" disabled={submitting} data-testid="member-request-submit"
              className="px-5 py-2 bg-[#9D00FF] text-white mono-font text-xs uppercase tracking-[0.25em] hover:bg-white hover:text-black transition-colors duration-200 disabled:opacity-60">
              {submitting ? "Sending…" : "Send Request"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
